import { httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { functions, db } from '../lib/firebase';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  Timestamp 
} from 'firebase/firestore';
import { InvoiceRequest, ProformaRequest, InvoiceListItem, BillingSettings } from '../types/billing';

/**
 * Create invoice via Firebase Function (Számlázz.hu API)
 */
export const createInvoice = async (
  invoiceData: InvoiceRequest,
  organizationId: string
): Promise<any> => {
  const createInvoiceFn = httpsCallable(functions, 'createInvoice');
  
  try {
    const result = await createInvoiceFn({
      invoiceData,
      organizationId
    });
    
    return result.data;
  } catch (error: any) {
    console.error('Error calling createInvoice function:', error);
    throw new Error(error.message || 'Failed to create invoice');
  }
};

interface PODDetail {
  podId: string;
  address?: string;
  partnerId?: string;
  consumption: number;
  itemName: string;
  unitPrice: number;
  amount: number;
  taxRate?: number;
}

const ATTACHMENT_SERVICE_URL = process.env.REACT_APP_ATTACHMENT_SERVICE_URL || 'https://invoice-attachment-service-789959682965.europe-west1.run.app';

/**
 * Create invoice with PDF + Excel attachments via Python Cloud Run service
 * (Combines szamlazz.hu + attachment generation in one backend call)
 */
export const createInvoiceWithAttachments = async (
  invoiceData: InvoiceRequest,
  organizationId: string,
  podDetails?: PODDetail[],
  billingPeriod?: { year: number; month: number },
  attachmentCode?: string,
  eurHufRate?: number
): Promise<any> => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();

  if (!ATTACHMENT_SERVICE_URL) throw new Error('ATTACHMENT_SERVICE_URL not configured');

  const response = await fetch(`${ATTACHMENT_SERVICE_URL}/create-invoice`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      invoiceData,
      organizationId,
      podDetails,
      billingPeriod,
      attachmentCode,
      eurHufRate,
    }),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Failed to create invoice with attachments');
  }
  return data;
};

/**
 * Validate Számlázz.hu agent key
 */
export const validateAgentKey = async (agentKey: string): Promise<any> => {
  const validateAgentKeyFn = httpsCallable(functions, 'validateAgentKey');
  
  try {
    const result = await validateAgentKeyFn({ agentKey });
    return result.data;
  } catch (error: any) {
    console.error('Error validating agent key:', error);
    throw new Error(error.message || 'Failed to validate agent key');
  }
};

/**
 * Save draft invoice to Firestore
 */
export const saveDraftInvoice = async (
  invoiceData: InvoiceRequest,
  organizationId: string
): Promise<string> => {
  try {
    const docRef = await addDoc(collection(db, 'invoiceDrafts'), {
      ...invoiceData,
      organizationId,
      invoiceType: 'INVOICE',
      status: 'DRAFT',
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error saving draft invoice:', error);
    throw error;
  }
};

/**
 * Save draft proforma to Firestore
 */
export const saveDraftProforma = async (
  proformaData: ProformaRequest,
  organizationId: string
): Promise<string> => {
  try {
    const docRef = await addDoc(collection(db, 'invoiceDrafts'), {
      ...proformaData,
      organizationId,
      invoiceType: 'PROFORMA',
      status: 'DRAFT',
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error saving draft proforma:', error);
    throw error;
  }
};

/**
 * Update draft invoice
 */
export const updateDraftInvoice = async (
  draftId: string,
  invoiceData: Partial<InvoiceRequest>,
  organizationId: string
): Promise<void> => {
  try {
    const draftRef = doc(db, 'invoiceDrafts', draftId);
    await updateDoc(draftRef, {
      ...invoiceData,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error updating draft invoice:', error);
    throw error;
  }
};

/**
 * Delete draft invoice
 */
export const deleteDraftInvoice = async (draftId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, 'invoiceDrafts', draftId));
  } catch (error) {
    console.error('Error deleting draft invoice:', error);
    throw error;
  }
};

/**
 * Get draft invoice by ID
 */
export const getDraftInvoice = async (draftId: string): Promise<any> => {
  try {
    const draftRef = doc(db, 'invoiceDrafts', draftId);
    const draftSnap = await getDocs(query(collection(db, 'invoiceDrafts'), where('__name__', '==', draftId)));
    
    if (draftSnap.empty) {
      throw new Error('Draft not found');
    }
    
    return {
      id: draftSnap.docs[0].id,
      ...draftSnap.docs[0].data()
    };
  } catch (error) {
    console.error('Error getting draft invoice:', error);
    throw error;
  }
};

/**
 * List invoices (both drafts and generated)
 */
export const listInvoices = async (organizationId: string): Promise<InvoiceListItem[]> => {
  try {
    // Get drafts
    const draftsQuery = query(
      collection(db, 'invoiceDrafts'),
      where('organizationId', '==', organizationId),
      orderBy('createdAt', 'desc')
    );
    const draftsSnap = await getDocs(draftsQuery);
    const drafts = draftsSnap.docs.map(doc => ({
      id: `draft_${doc.id}`,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate()
    })) as InvoiceListItem[];
    
    // Get generated invoices
    const invoicesQuery = query(
      collection(db, 'invoices'),
      where('organizationId', '==', organizationId),
      orderBy('createdAt', 'desc')
    );
    const invoicesSnap = await getDocs(invoicesQuery);
    const invoices = invoicesSnap.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      issueDate: doc.data().issueDate?.toDate(),
      dueDate: doc.data().dueDate?.toDate(),
      paidDate: doc.data().paidDate?.toDate(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate()
    })) as InvoiceListItem[];
    
    return [...drafts, ...invoices];
  } catch (error) {
    console.error('Error listing invoices:', error);
    throw error;
  }
};

/**
 * Generate live invoice from draft
 */
export const generateLiveInvoice = async (
  draftId: string,
  organizationId: string
): Promise<any> => {
  try {
    // Get draft data
    const draft = await getDraftInvoice(draftId);
    
    // Create invoice via Számlázz.hu
    const result = await createInvoice(draft, organizationId);
    
    // Delete draft after successful generation
    if (result.success) {
      await deleteDraftInvoice(draftId.replace('draft_', ''));
    }
    
    return result;
  } catch (error) {
    console.error('Error generating live invoice:', error);
    throw error;
  }
};

/**
 * Get billing settings for organization
 */
export const getBillingSettings = async (organizationId: string): Promise<BillingSettings | null> => {
  try {
    const settingsQuery = query(
      collection(db, 'billingSettings'),
      where('organizationId', '==', organizationId),
      where('isActive', '==', true)
    );
    const settingsSnap = await getDocs(settingsQuery);
    
    if (settingsSnap.empty) {
      return null;
    }
    
    const doc = settingsSnap.docs[0];
    return {
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate()
    } as BillingSettings;
  } catch (error) {
    console.error('Error getting billing settings:', error);
    throw error;
  }
};

/**
 * Save billing settings
 */
export const saveBillingSettings = async (
  settings: Omit<BillingSettings, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> => {
  try {
    // Deactivate existing settings
    const existingQuery = query(
      collection(db, 'billingSettings'),
      where('organizationId', '==', settings.organizationId),
      where('isActive', '==', true)
    );
    const existingSnap = await getDocs(existingQuery);
    
    for (const doc of existingSnap.docs) {
      await updateDoc(doc.ref, { isActive: false, updatedAt: Timestamp.now() });
    }
    
    // Create new settings
    const docRef = await addDoc(collection(db, 'billingSettings'), {
      ...settings,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error saving billing settings:', error);
    throw error;
  }
};

/**
 * Update billing settings
 */
export const updateBillingSettings = async (
  settingsId: string,
  updates: Partial<BillingSettings>
): Promise<void> => {
  try {
    const settingsRef = doc(db, 'billingSettings', settingsId);
    await updateDoc(settingsRef, {
      ...updates,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error updating billing settings:', error);
    throw error;
  }
};

/**
 * Parse bulk invoice data from TSV format
 */
export const parseBulkInvoiceData = (tsvContent: string): any => {
  const lines = tsvContent.trim().split('\n');
  if (lines.length < 2) {
    throw new Error('Invalid file format: no data rows found');
  }
  
  const headers = lines[0].split('\t');
  const rows = [];
  const errors = [];
  
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split('\t');
    
    if (values.length !== headers.length) {
      errors.push(`Row ${i}: Column count mismatch`);
      continue;
    }
    
    try {
      const row = {
        customerName: values[0],
        city: values[1],
        postalCode: values[2],
        address: values[3],
        taxNumber: values[4],
        currency: values[5] as 'HUF' | 'EUR',
        performanceDate: values[6],
        paymentTerm: values[7],
        subject: values[8],
        energyPrice: parseFloat(values[9].replace(',', '.')),
        katFunds: parseFloat(values[10]),
        premiumFunds: parseFloat(values[11]),
        exciseTax: parseFloat(values[12]),
        coalRestructuring: parseFloat(values[13]),
        preferentialSupport: parseFloat(values[14]),
        cogenRestructuring: parseFloat(values[15]),
        energyEfficiency: parseFloat(values[16]),
        quantity: parseFloat(values[17])
      };
      
      rows.push(row);
    } catch (error) {
      errors.push(`Row ${i}: Failed to parse data - ${error}`);
    }
  }
  
  return {
    rows,
    totalCount: lines.length - 1,
    validCount: rows.length,
    invalidCount: errors.length,
    errors
  };
};

/**
 * Create bulk invoices from parsed data
 */
export const createBulkInvoices = async (
  rows: any[],
  organizationId: string,
  onProgress?: (current: number, total: number) => void
): Promise<{ success: number; failed: number; errors: string[] }> => {
  let success = 0;
  let failed = 0;
  const errors: string[] = [];
  
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    
    try {
      // Convert row to invoice request
      const invoiceData: InvoiceRequest = {
        buyer: {
          name: row.customerName,
          address: {
            city: row.city,
            postalCode: row.postalCode,
            street: row.address,
            country: 'Magyarország'
          },
          taxNumber: row.taxNumber
        },
        items: [
          {
            name: 'Energiadíj',
            quantity: row.quantity,
            unit: 'kWh',
            unitPrice: row.energyPrice,
            taxRate: 27
          },
          {
            name: 'KÁT pénzeszközök',
            quantity: row.quantity,
            unit: 'kWh',
            unitPrice: row.katFunds,
            taxRate: 27
          },
          {
            name: 'Prémium pénzeszköz',
            quantity: row.quantity,
            unit: 'kWh',
            unitPrice: row.premiumFunds,
            taxRate: 27
          },
          {
            name: 'Jövedéki adó',
            quantity: row.quantity,
            unit: 'kWh',
            unitPrice: row.exciseTax,
            taxRate: 27
          },
          {
            name: 'Szénipari szerk. átalak. tám',
            quantity: row.quantity,
            unit: 'kWh',
            unitPrice: row.coalRestructuring,
            taxRate: 27
          },
          {
            name: 'Kedv. áru v. energia ellát. tám',
            quantity: row.quantity,
            unit: 'kWh',
            unitPrice: row.preferentialSupport,
            taxRate: 27
          },
          {
            name: 'Kapcsolt term. szerk. átalak.',
            quantity: row.quantity,
            unit: 'kWh',
            unitPrice: row.cogenRestructuring,
            taxRate: 27
          },
          {
            name: 'Energiahatékonysági díj',
            quantity: row.quantity,
            unit: 'kWh',
            unitPrice: row.energyEfficiency,
            taxRate: 27
          }
        ].filter(item => item.unitPrice > 0), // Only include items with price > 0
        paymentMethod: 'TRANSFER',
        paymentDeadline: row.paymentTerm === '15nap' ? 15 : 30,
        currency: row.currency === 'Ft.' ? 'HUF' : 'EUR',
        notes: row.subject,
        performanceDate: row.performanceDate,
        organizationId
      };
      
      await createInvoice(invoiceData, organizationId);
      success++;
      
      if (onProgress) {
        onProgress(i + 1, rows.length);
      }
    } catch (error: any) {
      failed++;
      errors.push(`${row.customerName}: ${error.message}`);
    }
  }
  
  return { success, failed, errors };
};
